import React, { useState } from 'react';
import { Printer, Loader } from 'lucide-react';
import { bluetoothPrinter } from '../../services/bluetoothPrinter';
import { Order } from '../../services/dataService';

interface PrintReceiptButtonProps {
  order: Order;
  restaurantName?: string;
  variant?: 'primary' | 'secondary' | 'icon';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onPrintStart?: () => void;
  onPrintSuccess?: () => void;
  onPrintError?: (error: string) => void;
}

const PrintReceiptButton: React.FC<PrintReceiptButtonProps> = ({
  order,
  restaurantName = 'Cafelawn',
  variant = 'primary',
  size = 'md',
  className = '',
  onPrintStart,
  onPrintSuccess,
  onPrintError
}) => {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      onPrintStart?.();

      // Check if printer is connected
      const status = bluetoothPrinter.getConnectionStatus();
      if (!status.isConnected) {
        throw new Error('No printer connected. Please connect to a Bluetooth printer first.');
      }

      // Print the receipt
      await bluetoothPrinter.printReceipt(order, restaurantName);
      
      onPrintSuccess?.();
    } catch (error) {
      console.error('Error printing receipt:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to print receipt';
      onPrintError?.(errorMessage);
    } finally {
      setIsPrinting(false);
    }
  };

  const getButtonClasses = () => {
    const baseClasses = 'flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const variantClasses = {
      primary: 'bg-blue-600 text-white hover:bg-blue-700',
      secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600',
      icon: 'bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-2 py-1 rounded border border-blue-200 dark:border-blue-600'
    };

    const sizeClasses = {
      sm: 'px-3 py-2 text-xs rounded-lg',
      md: 'px-4 py-2 text-sm rounded-lg',
      lg: 'px-5 py-3 text-base rounded-lg'
    };

    return `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm': return 'w-3 h-3';
      case 'md': return 'w-4 h-4';
      case 'lg': return 'w-5 h-5';
      default: return 'w-4 h-4';
    }
  };

  const showText = variant !== 'icon';

  return (
    <button
      onClick={handlePrint}
      disabled={isPrinting}
      className={getButtonClasses()}
      title="Print Receipt"
    >
      {isPrinting ? (
        <Loader className={`${getIconSize()} animate-spin`} />
      ) : (
        <Printer className={getIconSize()} />
      )}
      
      {showText && (
        <span className="ml-2">
          {isPrinting ? 'Printing...' : 'Print Receipt'}
        </span>
      )}
    </button>
  );
};

export default PrintReceiptButton; 